/** Scale status pill (top bar) and the connect controls used on several pages. */
import { Link, NavLink } from 'react-router-dom';
import { useScale, isBluetoothSupported, pickerListsEveryDevice, type DriverQuestionState } from '@/scale/store';
import { driverDescriptor } from '@/scale/registry';
import { isBluefy } from '@/lib/platform';
import type { ScaleWarning } from '@/scale/types';
import { fmtWeight } from '@/lib/format';
import { Icon } from './icons';
import { Badge, Banner, Button, Spinner, useToast } from './ui';

export function ScaleStatusPill() {
  const status = useScale((s) => s.status);
  const stale = useScale((s) => s.stale);
  const warning = useScale((s) => s.warning);
  const weight = useScale((s) => s.reading?.weightG ?? null);
  const battery = useScale((s) => s.batteryPct);
  const kind = useScale((s) => s.kind);
  const dotClass =
    status === 'connected' ? (warning ? 'err' : 'ok') : status === 'connecting' || status === 'reconnecting' ? 'busy' : status === 'error' ? 'err' : '';
  const idleLabel = status === 'connecting' ? 'Connecting…' : status === 'reconnecting' ? (stale ? 'No data…' : 'Reconnecting…') : 'No scale';
  return (
    // A NavLink: on phones, where Scale has no tab, the pill marks the Scale page as current (styles.css).
    <NavLink to="/scale" className="scale-pill" title={warning?.message ?? 'Scale'}>
      <span className={`dot ${dotClass}`} />
      {status === 'connected' ? (
        <>
          <span className="mono">{fmtWeight(weight)}</span>
          {import.meta.env.DEV && kind === 'simulator' && <span className="badge">SIM</span>}
          {battery != null && (
            <span className="row faint text-xs pill-battery" style={{ gap: 3 }}>
              <Icon name="battery" size={14} />
              {battery}%
            </span>
          )}
        </>
      ) : (
        <span className="muted">{idleLabel}</span>
      )}
    </NavLink>
  );
}

/** Explains why a connected scale shows no weight, with the lenient-checksum escape hatch. */
function ScaleWarningBanner({ warning }: { warning: ScaleWarning }) {
  const acceptUnverifiedPackets = useScale((s) => s.acceptUnverifiedPackets);
  return (
    <Banner tone="danger" icon="bluetooth">
      <div className="stack-sm">
        <span>{warning.message}</span>
        {warning.code === 'checksum' && (
          <div>
            <Button size="sm" onClick={acceptUnverifiedPackets}>
              Accept unverified packets
            </Button>
          </div>
        )}
      </div>
    </Banner>
  );
}

/**
 * "Which scale is this?": the picked device's name matched no driver, and several drivers could talk to it or the one
 * that could uses a generic service (FFE0, FFF0) that plenty of non-scales have too. The answer is remembered for that
 * device, so reconnects don't ask again.
 */
function DriverQuestionPrompt({ question }: { question: DriverQuestionState }) {
  const answer = useScale((s) => s.answerDriverQuestion);
  const why =
    question.options.length > 1
      ? 'Its name doesn’t say, and several scales use the same Bluetooth service.'
      : 'Its name doesn’t say, and lots of Bluetooth devices that aren’t scales use the same service. If it isn’t this scale, cancel.';
  return (
    <div className="stack-sm" role="group" aria-label="Which scale is this?">
      <span className="text-sm">
        <b>Which scale is {question.deviceName ? `“${question.deviceName}”` : 'this'}?</b> {why} Taretto remembers your answer for this scale.
      </span>
      <div className="row-wrap">
        {question.options.map((o) => (
          <Button key={o.kind} size="sm" onClick={() => answer(o.kind)}>
            {o.label}
            {o.experimental && <Badge tone="warning">Experimental</Badge>}
          </Button>
        ))}
      </div>
    </div>
  );
}

/**
 * Connect / disconnect controls. Shows a helpful message when Web Bluetooth is unavailable
 * (insecure origin or unsupported browser): a banner, or a one-line hint in the compact variant. Dev builds
 * also offer the simulator. While connecting or reconnecting everything stays usable: Cancel stops the
 * attempt, and any other choice replaces it.
 */
/**
 * `primary: false` keeps Connect and Reconnect as plain buttons where another action leads (the setup checklist).
 * `large` gives the buttons arm's-length targets where connecting is the page's main action (the Scale page), and
 * leaves the no-Web-Bluetooth explanation to the pairing check that page shows beside them. `main` makes Connect (or
 * Reconnect) a full-width extra-large button, in the place of the action it unlocks (the brew setup's Start bar), with
 * the other choices in a row under it; while connecting, that button shows the spinner.
 */
export function ScaleConnectControls({ compact = false, primary = true, large = false, main = false }: { compact?: boolean; primary?: boolean; large?: boolean; main?: boolean }) {
  const status = useScale((s) => s.status);
  const error = useScale((s) => s.error);
  const warning = useScale((s) => s.warning);
  const stale = useScale((s) => s.stale);
  const deviceName = useScale((s) => s.deviceName);
  const kind = useScale((s) => s.kind);
  const driverQuestion = useScale((s) => s.driverQuestion);
  const knownScale = useScale((s) => s.knownScale);
  const connect = useScale((s) => s.connect);
  const reconnect = useScale((s) => s.reconnect);
  const disconnect = useScale((s) => s.disconnect);
  const toast = useToast();
  const btSupported = isBluetoothSupported();
  const busy = status === 'connecting' || status === 'reconnecting';
  const size = compact ? 'sm' : large ? 'lg' : 'md';

  if (status === 'connected') {
    return (
      <div className="stack-sm">
        <div className="row-wrap">
          <span className="row text-sm">
            <span className={`dot ${warning ? 'err' : 'ok'}`} /> {deviceName}
          </span>
          {driverDescriptor(kind)?.experimental && (
            <span title="Nobody has confirmed this scale works with Taretto yet. If something is off, copy the packet log on the Scale page into an issue.">
              <Badge tone="warning">Experimental</Badge>
            </span>
          )}
          <Button size="sm" variant="ghost" onClick={() => void disconnect()}>
            Disconnect
          </Button>
        </div>
        {warning && <ScaleWarningBanner warning={warning} />}
      </div>
    );
  }

  const run = async (action: () => Promise<void>) => {
    try {
      await action();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'error');
    }
  };
  // Call connect() synchronously inside the click: the Bluetooth picker needs the user activation.
  const choose = () => void run(() => connect());
  // Dev builds only: the static import.meta.env.DEV checks let the production build drop the simulator.
  const simulator = () => void run(() => connect({ kind: 'simulator' }));
  const again = () => void run(() => reconnect());
  // Escape hatch for Bluefy (iPad), whose device filters are quirky: list every nearby device instead.
  const showAll = () => void run(() => connect({ showAllDevices: true }));

  const busyLabel =
    status === 'connecting'
      ? `Connecting${deviceName ? ` to ${deviceName}` : ''}…`
      : stale
        ? 'No data from the scale, waiting…'
        : 'Reconnecting to the scale…';
  const offerReconnect = knownScale != null && !busy && btSupported;
  const offerShowAll = btSupported && !busy && !pickerListsEveryDevice() && (isBluefy() || error != null);
  const insecure = typeof window !== 'undefined' && !window.isSecureContext;

  const reconnectLabel = <span className="truncate">Reconnect {knownScale}</span>;
  const lead = !main ? null : busy ? (
    <Button variant="primary" size="xl" block loading>
      <span className="truncate">{busyLabel}</span>
    </Button>
  ) : offerReconnect ? (
    <Button variant="primary" size="xl" block icon="refresh" title={`Reconnect ${knownScale}`} onClick={again}>
      {reconnectLabel}
    </Button>
  ) : (
    <Button variant="primary" size="xl" block icon="bluetooth" disabled={!btSupported} onClick={choose}>
      Connect scale
    </Button>
  );
  const others = !main || offerReconnect || busy;

  return (
    <div className="stack-sm">
      {lead}
      {busy && !main && (
        <div className="row-wrap">
          <span className="row text-sm muted">
            <Spinner size={16} /> {busyLabel}
          </span>
          <Button size="sm" variant="ghost" icon="x" onClick={() => void disconnect()}>
            Cancel
          </Button>
        </div>
      )}
      {busy && driverQuestion && <DriverQuestionPrompt question={driverQuestion} />}
      {(others || import.meta.env.DEV) && (
        <div className="row-wrap">
          {main && busy && (
            <Button size={size} variant="ghost" icon="x" onClick={() => void disconnect()}>
              Cancel
            </Button>
          )}
          {offerReconnect && !main && (
            // Bluetooth names can be long: the label truncates in a narrow card, the tooltip has it in full.
            <Button variant={primary ? 'primary' : 'default'} size={size} icon="refresh" title={`Reconnect ${knownScale}`} onClick={again}>
              {reconnectLabel}
            </Button>
          )}
          {others && (
            <Button variant={offerReconnect || busy ? 'ghost' : primary ? 'primary' : 'default'} size={size} icon="bluetooth" disabled={!btSupported} onClick={choose}>
              {offerReconnect || busy ? 'Choose scale…' : 'Connect scale'}
            </Button>
          )}
          {import.meta.env.DEV && (
            <Button size={size} variant="ghost" icon="sparkles" onClick={simulator}>
              Simulator
            </Button>
          )}
        </div>
      )}
      {offerShowAll && (
        <div>
          <Button size="sm" variant="ghost" icon="search" onClick={showAll}>
            Scale not listed? Show all devices
          </Button>
        </div>
      )}
      {!btSupported && !compact && !large && !main && (
        <Banner tone="warning">
          Web Bluetooth isn&apos;t available here.{' '}
          {insecure
            ? 'It requires HTTPS: open the app via its https:// address (see Settings → HTTPS).'
            : 'On iPad or iPhone, open Taretto in the free Bluefy browser, since Safari and Chrome there have no Web Bluetooth. Elsewhere use Chrome or Edge.'}
        </Banner>
      )}
      {!btSupported && (compact || main) && (
        <span className="text-xs faint">
          {insecure ? (
            <>
              Bluetooth needs HTTPS. <Link to="/settings#https">How to set that up</Link>
            </>
          ) : (
            'No Web Bluetooth in this browser. Use Bluefy on iPad or iPhone, Chrome or Edge elsewhere.'
          )}
        </span>
      )}
      {error && !compact && !main && <Banner tone="danger">{error}</Banner>}
    </div>
  );
}
